import { useEffect, useState, useContext } from 'react';
import { RiFilePdfFill } from 'react-icons/ri';
import { MdTextSnippet, MdDataObject } from 'react-icons/md';
import { useMsal } from "@azure/msal-react";
import { getToken } from "../../../authConfig";
import { AppStateContext } from '../../../state/AppProvider';
import { resolveEntities } from '../../../api/api';

interface AssistantViewerProps {
    id: string;
    onCancel: () => void;
}

export const AssistantViewer = ({ id, onCancel }: AssistantViewerProps) => {
    const { instance } = useMsal()
    const appStateContext = useContext(AppStateContext);
    const [title, setTitle] = useState('');
    const [promptMessage, setPromptMessage] = useState('');
    const [assistantFiles, setAssistantFiles] = useState<any[]>([]);
    const [users, setUsers] = useState<any[]>([]);
    const [groups, setGroups] = useState<any[]>([])

    useEffect(() => {
        const fetchAssistantDetails = async () => {
            const assistants = appStateContext?.state.assistants || []
            const a: any = assistants.find((x: any) => x.id === id)
            if (!a) return

            setTitle(a.title)
            setPromptMessage(a.system_prompt_message)
            setAssistantFiles(a.files || [])

            const uids = a.userIds ?? []
            const gids = a.groups ?? []
            if (uids.length === 0 && gids.length === 0) return

            try {
                const token = instance ? await getToken(instance) : undefined;
                const ents = await resolveEntities(uids, gids, token?.accessToken)
                setUsers(ents.filter((e: any) => e.objectType === 'user'))
                setGroups(ents.filter((e: any) => e.objectType === 'group'))

            } catch (e) {
                console.error('Error resolving entities', e)
            }
        }

        fetchAssistantDetails()
    }, [id, instance, appStateContext])

    const formatFileSize = (size: number) => {
        if (size >= 1024 * 1024) {
            return (size / (1024 * 1024)).toFixed(2) + ' MB';
        } else if (size >= 1024) {
            return (size / 1024).toFixed(2) + ' KB';
        } else {
            return size + ' B';
        }
    };

    const getFileIcon = (fileName: string) => {
        const lower = fileName.toLowerCase();
        if (lower.endsWith('.txt')) {
            return <MdTextSnippet className="inline mr-2 text-eq-green" />;
        }
        if (lower.endsWith('.json')) {
            return <MdDataObject className="inline mr-2 text-eq-green" />;
        }
        return <RiFilePdfFill className="inline mr-2 text-eq-green" />;
    };

    return (
        <div className="relative flex flex-col h-full">
            {/* Header Sectionn */}
            <div className="sticky top-0 z-10 bg-white px-4 pt-6 pb-6 sm:px-6 border-b border-gray-200">
                <div className="flex items-start gap-4">
                    {/* Icon */}
                    <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-green-100">
                        <span className="material-symbols-outlined text-[1.5rem] text-green-600">visibility</span>
                    </div>
                    {/* Title & Description */}
                    <div>
                        <h3 className="text-lg font-semibold leading-6 text-gray-900">
                            View Assistant Details
                        </h3>
                        <p className="mt-1 text-sm text-gray-500">
                            Review this assistant's configuration, knowledge base, and access permissions.
                        </p>
                    </div>
                </div>
            </div>

            {/* Scrollable Content */}
            <div className="flex-grow px-8 py-6">
                {/* Title */}
                <div className="mb-6">
                    <h4 className="text-lg font-semibold text-eq-grey">
                        Assistant Name
                    </h4>
                    <div className="mt-2 block w-full rounded-md p-3 text-sm border border-eq-grey bg-eq-super-light-grey">
                        {title}
                    </div>
                </div>

                {/* Prompt Message */}
                <div className="mb-6">
                    <h4 className="text-lg font-semibold text-eq-grey">
                        System Prompt
                    </h4>
                    <div className="mt-2 block w-full rounded-md p-3 text-sm border border-eq-grey bg-eq-super-light-grey whitespace-pre-wrap">
                        {promptMessage}
                    </div>
                </div>

                {/* Shared With */}
                <div className="mb-6">
                    <h4 className="text-lg font-semibold text-eq-grey mb-3">Shared With</h4>

                    <p className="text-sm font-medium text-eq-grey mb-2">Users:</p>
                    {users.length ? (
                        <ul className="list-disc pl-5 text-eq-grey mb-4">
                            {users.map((u: any) => (
                                <li key={u.id}>{u.displayName} ({u.mail || 'no mail'})</li>
                            ))}
                        </ul>
                    ) : (
                        <div className="mb-4 block w-full rounded-md p-3 text-sm border border-eq-grey bg-eq-super-light-grey text-eq-grey">
                            No users shared.
                        </div>
                    )}

                    <p className="text-sm font-medium text-eq-grey mb-2">Groups:</p>
                    {groups.length ? (
                        <ul className="list-disc pl-5 text-eq-grey">
                            {groups.map((g: any) => (
                                <li key={g.id}>{g.displayName} ({g.mail || g.mailNickname || 'no mail'})</li>
                            ))}
                        </ul>
                    ) : (
                        <div className="block w-full rounded-md p-3 text-sm border border-eq-grey bg-eq-super-light-grey text-eq-grey">
                            No groups shared.
                        </div>
                    )}
                </div>

                {/* Documents */}
                <div className="mb-6">
                    <h4 className="text-lg font-semibold text-eq-grey mb-3">
                        Knowledge Base
                    </h4>
                    <p className="text-sm text-eq-grey mb-3">
                        Files attached to this assistant:
                    </p>
                    {assistantFiles.length > 0 ? (
                        <ul className="space-y-2">
                            {assistantFiles.map((file: any, index: number) => (
                                <li key={index} className="flex items-center text-eq-grey">
                                    {getFileIcon(file.name)}
                                    <span>{file.name}</span>
                                    {file.size && (
                                        <span className="ml-2 text-eq-grey text-sm">
                                            ({formatFileSize(file.size)})
                                        </span>
                                    )}
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <div className="block w-full rounded-md p-3 text-sm border border-eq-grey bg-eq-super-light-grey text-eq-grey">
                            No documents attached.
                        </div>
                    )}
                </div>
            </div>

            {/* Footer */}
            <div className="sticky bottom-0 z-10 border-t border-gray-200 bg-white flex flex-row gap-4 px-4 pb-6 pt-4 sm:flex-row-reverse sm:px-6">
                <button
                    className="eq-secondary-button hover:bg-eq-light-grey"
                    onClick={onCancel}
                >
                    Back
                </button>
            </div>
        </div>
    );
};
